import Button from '@/common/components/base/button/Button';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import FreeZonePlateInput from '@/common/components/base/input/plate/freeZonePlateInput/FreeZonePlateInput';
import { FreePlateEmptyRules } from '@/common/components/base/input/plate/freeZonePlateInput/freeZonePlateInput.rules';
import VehiclePlateInput from '@/common/components/base/input/plate/vehiclePlateInput/VehiclePlateInput';
import { VehiclePlateEmptyRules } from '@/common/components/base/input/plate/vehiclePlateInput/vehiclePlateInput.rules';
import LoadingPage from '@/common/pages/LoadingPage';
import {
  PUBLIC_REQUIRED_RULE,
  requiredText,
} from '@/common/utils/rules/publicRules';
import usePartOneHook from '@/modules/adjusterReportModules/IncidentParticipants/components/vehicleInformation/hooks/usePartOneHook';
import { VehicleInformationEnum } from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import ImagesFabMainModal from '@/modules/adjusterReportModules/adjusterReport/components/imagesFab/ImagesFabMainModal';
import { FabQueryTabStateType } from '@/modules/adjusterReportModules/adjusterReport/queries/imageFab/getFabTabs/getFabTabsQuery.type';
import { Controller, FormProvider } from 'react-hook-form';
import { Flex } from 'styled-system/jsx';

const CulpritVehicleInformationPartOne = ({
  type,
}: {
  type: VehicleInformationEnum;
}) => {
  const {
    VehiclePlateDesignWatch,
    VehicleTypeWatch,
    VehiclePlateTypeWatch,
    data,
    methods,
    observerRefCallback,
    setName,
    submitHandler,
    vehicleInformationLoading,
    vehicleInformationData,
  } = usePartOneHook(type);
  const stateHandler = (): FabQueryTabStateType => {
    switch (type) {
      case VehicleInformationEnum.culprit:
        return 'propertyCulprit';
      case VehicleInformationEnum.damaged:
        return 'propertyDamaged';
      default:
        return 'propertyCulprit';
    }
  };
  if (vehicleInformationLoading) {
    return <LoadingPage />;
  }

  return (
    <FormProvider {...methods}>
      <Flex direction="column" gap={1} mb={24}>
        <ModalInput
          name="vehicle_type"
          placeholder="نوع وسیله‌نقلیه"
          label="نوع وسیله‌نقلیه"
          title="نوع وسیله‌نقلیه"
          disabled={!!vehicleInformationData.vehicle_type?.id}
          options={data.vehicleTypes ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_model"
          placeholder="انتخاب کنید"
          label="سیستم و تیپ"
          title="سیستم و تیپ"
          options={data?.vehicleSystems ?? []}
          loading={data?.isVehicleSystemsPending}
          infiniteScrollRef={observerRefCallback}
          searchOptions={{
            onChange: (e) => {
              setName(e);
            },
            placeholder: 'جستجو کنید',
          }}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_use"
          placeholder="انتخاب کنید"
          label="مورد استفاده"
          title="مورد استفاده"
          loading={vehicleInformationLoading}
          options={data?.vehicleUses ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_plate_type"
          placeholder="انتخاب کنید"
          label="نوع پلاک"
          title="نوع پلاک"
          options={data?.plateTypes ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_plate_design"
          label="طرح پلاک"
          title="طرح پلاک"
          placeholder="انتخاب کنید"
          options={data?.plateDesigns ?? []}
          rules={{ required: { message: requiredText, value: true } }}
          onChange={() => {
            methods.setValue(
              'vehicle_plate.plate.vehicle_plate_bottom',
              undefined,
            );
            methods.setValue(
              'vehicle_plate.plate.vehicle_plate_top',
              undefined,
            );
          }}
        />
        <ModalInput
          name="vehicle_plate_sample"
          placeholder="انتخاب کنید"
          label="نمونه پلاک"
          title="نمونه پلاک"
          disabled={
            !VehiclePlateDesignWatch?.value ||
            !VehicleTypeWatch?.value ||
            !VehiclePlateTypeWatch?.value
          }
          options={data?.plateSamples ?? []}
          rules={data?.plateSamples?.length ? PUBLIC_REQUIRED_RULE : undefined}
          optionalLabel={!data?.plateSamples?.length}
        />
        {(VehiclePlateDesignWatch?.value === 'country' ||
          !VehiclePlateDesignWatch?.value) &&
        VehicleTypeWatch?.label !== 'موتورسیکلت' ? (
          <Controller
            name="vehicle_plate"
            rules={{
              validate: VehiclePlateEmptyRules,
            }}
            render={({ field, fieldState }) => {
              return (
                <VehiclePlateInput
                  onChange={(v) => {
                    field.onChange(v);
                    if (methods.formState.touchedFields.vehicle_plate) {
                      methods.trigger('vehicle_plate');
                    }
                    v.plateString && methods.setValue('vehicle_plate', v);
                  }}
                  key={VehiclePlateDesignWatch?.value}
                  value={field.value}
                  requiredError={fieldState.error?.message}
                />
              );
            }}
          />
        ) : (
          <Controller
            name="vehicle_plate"
            rules={{
              validate: (value) =>
                FreePlateEmptyRules(
                  value,
                  VehicleTypeWatch?.label === 'موتورسیکلت' &&
                    VehiclePlateDesignWatch?.value === 'free_zone',
                ),
            }}
            render={({ field, fieldState }) => {
              return (
                <FreeZonePlateInput
                  onChange={(v) => {
                    field.onChange(v);
                    if (methods.formState.touchedFields.vehicle_plate) {
                      methods.trigger('vehicle_plate');
                    }
                    v.plateString && methods?.setValue('vehicle_plate', v);
                  }}
                  value={methods.getValues('vehicle_plate')}
                  requiredError={fieldState.error?.message}
                  vehicle_plate_design={
                    VehiclePlateDesignWatch?.value ?? 'country'
                  }
                  vehicle_type_id={VehicleTypeWatch?.value ?? 1}
                  key={VehiclePlateDesignWatch?.value}
                />
              );
            }}
          />
        )}
        <Button
          onClick={methods?.handleSubmit(submitHandler)}
          type="submit"
          width="100%"
          marginTop="32px"
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </Button>
      </Flex>
      <ImagesFabMainModal state={stateHandler()} />
    </FormProvider>
  );
};
export default CulpritVehicleInformationPartOne;
